/**
 * `SearchEmptyState` — the outcome page of a deterministic search that came
 * back with nothing to list: flight AND hotel, one component. The Figma frame
 * (`Determinístico hotel / Não retornou busca`, node 437:25105) draws the
 * layout by hand and instantiates only the two `VOLL/Button`s, so this is a
 * composition of the DS `Empty` slots with the DS `Button`, not a new
 * primitive.
 *
 * Two tones share the SAME look and differ only in what assistive tech hears:
 *
 * - `empty` — the supplier answered and the answer is "there is none". The
 *   list arrives async after the search, so the outcome changes the screen
 *   without a gesture at that instant; the title is announced politely
 *   (`role="status"`), because nothing went wrong.
 * - `error` — the search itself failed upstream. Same page, same actions, but
 *   the title is assertive (`role="alert"`): it is a failure the person must
 *   notice, and the hosts' tests key off that role.
 *
 * Each action renders only with its callback. A frozen/readOnly host passes
 * neither and gets a page with no actions: it can neither re-run nor leave, so
 * offering either would be a lie. The title always stays — the page still has
 * to say what happened. Copy comes from the host (i18n lives outside this kit).
 *
 * The figure is decorative (`alt=""`): the title next to it already says what
 * the page is, so the art repeats nothing to a screen reader.
 */
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@voll-intelligence/ui-kit/ui/empty';
import compassMap from './assets/empty-search-compass.png';

export interface SearchEmptyStateCopy {
  /** Secondary action: back to the planner with the fields still filled in. */
  readonly newSearchLabel: string;
  /** Primary action: re-run the SAME search. */
  readonly retryLabel: string;
}

export interface SearchEmptyStateProps {
  readonly tone: 'empty' | 'error';
  readonly title: string;
  /** Optional second line under the title (the frame carries a hidden slot for it). */
  readonly description?: string | undefined;
  readonly copy: SearchEmptyStateCopy;
  readonly onNewSearch?: (() => void) | undefined;
  readonly onRetry?: (() => void) | undefined;
}

export function SearchEmptyState({
  tone,
  title,
  description,
  copy,
  onNewSearch,
  onRetry,
}: SearchEmptyStateProps) {
  const hasActions = Boolean(onNewSearch) || Boolean(onRetry);
  return (
    // Frame rhythm: 32px from the figure to the message, 24px from the message
    // block to the actions, 10px between the buttons. `Empty` ships 16/8, so the
    // gaps are widened here; the dashed border of the primitive has zero width
    // and stays invisible, which is what the full-bleed page wants.
    <Empty className="gap-6">
      <EmptyHeader className="max-w-md gap-8">
        {/* 150×100 slot of the drawing; the file is the @4x render. The slot's
            own `mb-2` would stack on the header gap and overshoot the frame. */}
        <EmptyMedia className="mb-0">
          <img
            className="h-[100px] w-[150px] max-w-full object-contain select-none"
            src={compassMap}
            alt=""
            draggable={false}
          />
        </EmptyMedia>
        <div className="flex flex-col items-center gap-1">
          <EmptyTitle
            role={tone === 'error' ? 'alert' : 'status'}
            // Frame: Text-base/Semi Bold (16/24). The slot default is 14/medium.
            className="text-base leading-6 font-semibold tracking-normal"
          >
            {title}
          </EmptyTitle>
          {description ? <EmptyDescription>{description}</EmptyDescription> : null}
        </div>
      </EmptyHeader>
      {hasActions ? (
        <EmptyContent className="flex-row justify-center">
          {onNewSearch ? (
            <Button variant="outline" size="lg" onClick={onNewSearch}>
              {copy.newSearchLabel}
            </Button>
          ) : null}
          {onRetry ? (
            <Button size="lg" onClick={onRetry}>
              {copy.retryLabel}
            </Button>
          ) : null}
        </EmptyContent>
      ) : null}
    </Empty>
  );
}
